import React from 'react';
import { useSpc } from '../../context/SpcContext';
import { Reveal } from '../ui';
import { ClipboardList, ShieldAlert, FolderLock, UserCheck, ChevronLeft, Upload, Loader2, Map, ExternalLink, Download, Pencil, Trash2 } from 'lucide-react';
import Poll from './Poll';
import { ResourceCard } from './Resources';
import { SessionSafetyCards } from '../Modals';
import { ImageGallery } from '../ImageGallery';

function BackHead({ title, subtitle, onBack, action }) {
  return (
    <Reveal className="section-head">
      <div>
        <button type="button" className="btn ghost" onClick={onBack} data-testid="coach-hub-back-btn" style={{ marginBottom: 10 }}><ChevronLeft size={14} /> Coaches / Leaders</button>
        <h2>{title}</h2>
        {subtitle && <p>{subtitle}</p>}
      </div>
      {action}
    </Reveal>
  );
}

function SafetyCardsPanel({ user, onBack }) {
  const ctx = useSpc();
  const { state } = ctx;
  const canOpen = user?.role === 'Admin' || (user?.permissions || []).includes('create_sessions');
  const todayISO = new Date().toISOString().slice(0, 10);
  const todays = (state.sessions || [])
    .filter(s => s.dateISO === todayISO)
    .sort((a, b) => (a.time || '').localeCompare(b.time || ''));

  return (
    <div data-testid="coach-hub-safety-view">
      <BackHead title="Safety Cards" subtitle="Every booked member's ICE (In Case of Emergency) contact & medical info — pulled straight from their own Safety Card, for quick access while a session is running." onBack={onBack} />
      {!canOpen ? (
        <div className="empty" data-testid="coach-hub-safety-no-perm"><ShieldAlert size={26} /><p>You need session management access to view safety cards — ask an admin to grant it.</p></div>
      ) : !todays.length ? (
        <div className="empty" data-testid="coach-hub-safety-empty"><ShieldAlert size={26} /><p>No sessions running today — safety cards for a session show up here on the day it runs.</p></div>
      ) : (
        <div className="list" data-testid="coach-hub-safety-sessions">
          {todays.map(s => (
            <div key={s.id} data-testid={`coach-hub-safety-session-${s.id}`}>
              <Reveal className="card span-12">
                <div className="item-title" style={{ marginBottom: 8 }}>{s.title} <span className="pill soft">{s.time}</span></div>
                {s.booked ? <SessionSafetyCards ctx={ctx} s={s} /> : <p className="meta">No one booked yet.</p>}
              </Reveal>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PrivateResourcesPanel({ role, onBack }) {
  const { openModal, getCoachHubResources, removeResource, resourceFileUrl } = useSpc();
  const [items, setItems] = React.useState(null);

  const load = React.useCallback(async () => setItems(await getCoachHubResources()), [getCoachHubResources]);
  React.useEffect(() => { load(); }, [load]);

  const onEdit = (r) => openModal('editResource', { resource: r, forcePrivate: true, onDone: load });
  const onDelete = async (id) => { if (window.confirm('Remove this resource?')) { await removeResource(id); load(); } };

  return (
    <div data-testid="coach-hub-resources-view">
      <BackHead title="Private resources" subtitle="Images, guides & files for coaches and run leaders only — never shown on the public Resource Library." onBack={onBack}
        action={role === 'admin' && <button className="btn" onClick={() => openModal('newResource', { forcePrivate: true, onDone: load })} data-testid="coach-hub-upload-btn"><Upload size={15} /> Upload</button>} />
      {items === null ? (
        <div className="empty"><Loader2 size={20} className="spin" /> Loading…</div>
      ) : !items.length ? (
        <div className="empty" data-testid="coach-hub-resources-empty"><FolderLock size={26} /><p>No private resources yet.</p></div>
      ) : (
        <div className="grid" data-testid="coach-hub-resources-grid">
          {items.map(r => <ResourceCard key={r.id} r={r} role={role} resourceFileUrl={resourceFileUrl} onEdit={onEdit} onDelete={onDelete} />)}
        </div>
      )}
    </div>
  );
}

function RouteCard({ r, role, routePhotoUrl, routeGpxUrl, onEdit, onDelete }) {
  return (
    <div className="card span-4 resource-card" data-testid={`route-card-${r.id}`}>
      {r.hasPhoto ? <ImageGallery photos={[routePhotoUrl(r.id, r.photoV)]} alt={r.title} testId={`route-gallery-${r.id}`} /> : <div className="resource-icon"><Map size={24} /></div>}
      <div className="bc-badges" style={{ marginTop: 12 }}>
        {r.distance && <span className="pill soft">{r.distance}</span>}
        {r.elevation && <span className="pill soft">{r.elevation}</span>}
        {r.difficulty && <span className="pill soft">{r.difficulty}</span>}
      </div>
      <h3 style={{ marginTop: 10 }}>{r.title}</h3>
      {r.startLocation && <div className="meta">Starts at {r.startLocation}</div>}
      {r.description && <p>{r.description}</p>}
      <div className="meta">Added {r.createdAt} · {r.uploadedBy}</div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
        {r.externalLink && <a className="btn secondary" href={r.externalLink} target="_blank" rel="noopener noreferrer" data-testid={`route-open-link-${r.id}`}><ExternalLink size={14} /> Open route</a>}
        {r.hasGpx && <a className="btn secondary" href={routeGpxUrl(r.id)} data-testid={`route-download-gpx-${r.id}`}><Download size={14} /> GPX file</a>}
        {!r.externalLink && !r.hasGpx && <span className="pill soft">No link or GPS file attached</span>}
        {role === 'admin' && (
          <>
            <button className="btn ghost" onClick={() => onEdit(r)} data-testid={`route-edit-${r.id}`}><Pencil size={13} /></button>
            <button className="btn danger" onClick={() => onDelete(r.id)} data-testid={`route-delete-${r.id}`}><Trash2 size={13} /></button>
          </>
        )}
      </div>
    </div>
  );
}

function RoutesPanel({ role, onBack }) {
  const { openModal, getCoachHubRoutes, removeRoute, routePhotoUrl, routeGpxUrl } = useSpc();
  const [items, setItems] = React.useState(null);

  const load = React.useCallback(async () => setItems(await getCoachHubRoutes()), [getCoachHubRoutes]);
  React.useEffect(() => { load(); }, [load]);

  const onEdit = (r) => openModal('editRoute', { route: r, onDone: load });
  const onDelete = async (id) => { if (window.confirm('Remove this route?')) { await removeRoute(id); load(); } };

  return (
    <div data-testid="coach-hub-routes-view">
      <BackHead title="Routes" subtitle="Run routes for coaches and run leaders only — photo, distance/elevation, a Strava/Komoot link and a downloadable GPS file." onBack={onBack}
        action={role === 'admin' && <button className="btn" onClick={() => openModal('newRoute', { onDone: load })} data-testid="coach-hub-add-route-btn"><Upload size={15} /> Add route</button>} />
      {items === null ? (
        <div className="empty"><Loader2 size={20} className="spin" /> Loading…</div>
      ) : !items.length ? (
        <div className="empty" data-testid="coach-hub-routes-empty"><Map size={26} /><p>No routes yet.</p></div>
      ) : (
        <div className="grid" data-testid="coach-hub-routes-grid">
          {items.map(r => <RouteCard key={r.id} r={r} role={role} routePhotoUrl={routePhotoUrl} routeGpxUrl={routeGpxUrl} onEdit={onEdit} onDelete={onDelete} />)}
        </div>
      )}
    </div>
  );
}

export default function CoachHub() {
  const { role, user, openModal } = useSpc();
  const [tab, setTab] = React.useState('landing');
  const back = () => setTab('landing');
  const canCheckin = role === 'admin' || !!user?.canScan;
  const canSee = role === 'admin' || (user?.permissions || []).includes('view_polls') || canCheckin;

  if (!canSee) {
    return (
      <div data-testid="coach-hub-view">
        <Reveal className="section-head">
          <div><div className="eyebrow">Leaders only</div><h2>Coaches &amp; Leaders Hub</h2><p>This area is only visible to Coaches and Run Leaders.</p></div>
        </Reveal>
        <div className="empty"><ShieldAlert size={26} /><p>Ask an admin to promote you if you should be helping lead sessions.</p></div>
      </div>
    );
  }

  if (tab === 'poll') return (<div data-testid="coach-hub-poll-view"><BackHead title="Weekly poll" onBack={back} /><Poll /></div>);
  if (tab === 'safety') return <SafetyCardsPanel user={user} onBack={back} />;
  if (tab === 'resources') return <PrivateResourcesPanel role={role} onBack={back} />;
  if (tab === 'routes') return <RoutesPanel role={role} onBack={back} />;

  return (
    <div data-testid="coach-hub-view">
      <Reveal className="section-head">
        <div>
          <div className="eyebrow">Leaders only</div>
          <h2>Coaches &amp; Leaders Hub</h2>
          <p>Everything for the people who run SPC sessions — the weekly poll, safety cards and private resources.</p>
        </div>
      </Reveal>
      <div className="grid coach-hub-grid" data-testid="coach-hub-cards">
        <button type="button" className="card span-4 coach-hub-card" data-testid="coach-hub-card-poll" onClick={() => setTab('poll')}>
          <div className="coach-hub-card-icon"><ClipboardList size={24} /></div>
          <h3>Weekly poll</h3>
          <p>Tick the days you can lead a session this week.</p>
        </button>
        <button type="button" className="card span-4 coach-hub-card" data-testid="coach-hub-card-safety" onClick={() => setTab('safety')}>
          <div className="coach-hub-card-icon"><ShieldAlert size={24} /></div>
          <h3>Safety Cards</h3>
          <p>Today's booked members' ICE contacts &amp; medical info, ready during a live session.</p>
        </button>
        <button type="button" className="card span-4 coach-hub-card" data-testid="coach-hub-card-resources" onClick={() => setTab('resources')}>
          <div className="coach-hub-card-icon"><FolderLock size={24} /></div>
          <h3>Private resources</h3>
          <p>Images &amp; files for coaches and leaders only.</p>
        </button>
        <button type="button" className="card span-4 coach-hub-card" data-testid="coach-hub-card-routes" onClick={() => setTab('routes')}>
          <div className="coach-hub-card-icon"><Map size={24} /></div>
          <h3>Routes</h3>
          <p>Run routes with a photo, distance/elevation and a GPS file — coaches and leaders only.</p>
        </button>
        {canCheckin && (
          <button type="button" className="card span-4 coach-hub-card" data-testid="coach-hub-card-checkin" onClick={() => openModal('adminCheckIn')}>
            <div className="coach-hub-card-icon"><UserCheck size={24} /></div>
            <h3>Check in members</h3>
            <p>Scan or enter a member's code to check them into a live session.</p>
          </button>
        )}
      </div>
    </div>
  );
}
